/* eslint-disable prettier/prettier -- behoud de afgesproken witregels binnen conditionals. */
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useInsertionEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { Env } from './environment'
import { applyMaterial } from './createMaterial'
import {
  loadTexturesFromJSON,
  unloadTextures,
  disposeTextures,
} from './loadTexture'
import { loadModel, disposeModel } from './loadModel'
import { createAssetRequestScope, isAbortError } from './assetRequests'
import { RendererSettings, RendererInfo } from './renderSettings'
import { CameraUpdater } from './cameraUpdater'
import CaptureComponent from './capture'
import { findProductData, getMaterialTypes } from './utilities'
import ExportBridge from './exports'
import { AnimationClip, AnimationMixer, Box3, LoopOnce, Vector3 } from 'three'

const isDevelopment =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'

export function Viewer({ onUnmount, ...props }) {
  const onUnmountRef = useRef(onUnmount)

  useLayoutEffect(() => {
    onUnmountRef.current = onUnmount
  }, [onUnmount])

  // volg net als de Canvas de echte verwijdering, zonder StrictMode-replay.
  useInsertionEffect(() => () => {
    queueMicrotask(() => {
      if (typeof onUnmountRef.current === 'function') {

        onUnmountRef.current()

      }
    })
  }, [])

  // geef een ander hoofdproduct een eigen scène, zodat modellen en camera resetten.
  return <ViewerScene key={props.productData?.[0]?.modelCode} {...props} />
}

function ViewerScene({
  productData: productDataArray,
  resolution = 'medium',
  onViewerReady,
  onViewerError,
  environment = 'brown_photostudio_05_1k',
  className,
  containerClassName,
  onCapture,
  castShadow = false,
  onARExport,
  needDataOnViewerReady = false,
  onViewerAPI,
}) {
  const [error, setError] = useState(false)
  const [envError, setEnvError] = useState(false)
  const [envReady, setEnvReady] = useState(false)
  const [models, setModels] = useState([])
  const [materialsMap, setMaterialsMap] = useState(new Map())
  const [texturesLoaded, setTexturesLoaded] = useState(false)
  const [modelsLoaded, setModelsLoaded] = useState(false)
  const [viewerReady, setViewerReady] = useState(false)
  const [texturesVersion, bumpTexturesVersion] = useState(0)
  const [meshes, setMeshes] = useState([])
  const sceneRef = useRef(null)

  const [requestScope] = useState(createAssetRequestScope)

  const modelRequestsRef = useRef(new Map())

  const ownedModelsRef = useRef(new Set())

  const modelsRef = useRef(models)

  const materialsMapRef = useRef(materialsMap)

  const onViewerErrorRef = useRef(onViewerError)

  const unmountedRef = useRef(false)

  const readyFrameRef = useRef(null)

  useLayoutEffect(() => {
    modelsRef.current = models
    onViewerErrorRef.current = onViewerError
  }, [models, onViewerError])

  useInsertionEffect(() => {
    return () => {
      unmountedRef.current = true

      // stop ook environmentrequests direct, vóór de asynchrone R3F-opruiming.
      requestScope.abort()
      modelRequestsRef.current.clear()

      if (readyFrameRef.current !== null) {

        cancelAnimationFrame(readyFrameRef.current)
        readyFrameRef.current = null

      }

      queueMicrotask(() => {
        ownedModelsRef.current.forEach((model) => disposeModel(model))
        ownedModelsRef.current.clear()

        materialsMapRef.current.forEach(({ textures }) => disposeTextures(textures))
        materialsMapRef.current.clear()
      })
    }
  }, [requestScope])

  const lastBumpedVersionRef = useRef(0)
  const inFlightVersionRef = useRef(0)
  const modelsCount = useRef(0)

  const [sceneMounted, setSceneMounted] = useState(false)
  const mountedUUIDsRef = useRef(new Set()) // refs van de modellen bijhouden die gemounted zijn in de scene

  // de laatste selectie tracken, zodat we een diff kunnen doen
  const prevSelectionRef = useRef(new Map())

  // enkel de veranderde paren
  const [changeSet, setChangeSet] = useState({
    keys: new Set(),
    materials: new Set(),
    version: 0,
  })

  const refreshMeshes = useCallback(() => {
    const scene = sceneRef.current
    if (!scene) return

    scene.updateMatrixWorld(true)

    const found = []
    scene.traverse((o) => {
      if (o.isMesh) found.push(o)
    })

    setMeshes(found)
  })

  const filteredProductDataArray = useMemo(() => {
    if (!productDataArray.length) return productDataArray

    console.log(`👀 incoming productData:`, productDataArray)

    return productDataArray.map((product) => {
      if (!product.crossSellData) return product

      // vermijd dubbele xsells by modelCode
      const filtered = new Set()
      const filteredCrossSell = product.crossSellData.filter((item) => {
        if (filtered.has(item.modelCode)) return false
        filtered.add(item.modelCode)
        return true
      })

      return {
        ...product,
        crossSellData: filteredCrossSell,
      }
    })
  }, [productDataArray])

  const requiredModelCodes = JSON.stringify(
    [...new Set(filteredProductDataArray.flatMap((product) => [
      product.modelCode,
      ...(product.crossSellData || []).map((item) => item.modelCode),
    ]))].sort(),
  )

  const productDataRef = useRef(filteredProductDataArray)

  useLayoutEffect(() => {
    productDataRef.current = filteredProductDataArray
  }, [filteredProductDataArray])

  // is data set data
  const isSet = useMemo(() => {
    if (!filteredProductDataArray?.length) return false

    if (filteredProductDataArray[0]?.modelCode) {
      return /SET\d{2}/i.test(filteredProductDataArray[0].modelCode)
    }

    return false
  }, [filteredProductDataArray])

  // map voor de materials die in actieve selectie zijn (modelCode:type)
  function buildSelectionMap(productData) {
    const map = new Map()
    for (const p of productData) {
      for (const m of p.materialData || []) {
        map.set(`${p.modelCode}:${m.type}`, {
          code: m.code,
          material: m.material,
        })
      }
      for (const cs of p.crossSellData || []) {
        for (const m of cs.materialData || []) {
          map.set(`${cs.modelCode}:${m.type}`, {
            code: m.code,
            material: m.material,
          })
        }
      }
    }
    return map
  }

  // vind relevante sockets
  function findRelevantSockets(modelCode, sockets) {
    if (!Array.isArray(sockets) || !modelCode) return []

    // action match eerst
    let relevant = sockets.filter(
      (socket) =>
        socket.name.startsWith(`socket_${modelCode}`) &&
        socket.name.endsWith(`Action`),
    )

    // hard match erna
    if (!relevant.length) {
      relevant = sockets.filter(
        (socket) =>
          (socket.name.startsWith(`socket_${modelCode}`) &&
            socket.name.endsWith(`socket_${modelCode}`)) ||
          socket.name.startsWith(`socket_${modelCode}0`) ||
          socket.name.startsWith(`socket_${modelCode}_`),
      )
    }

    if (!relevant.length) {
      // fuzzy match, indien geen matches
      relevant = sockets.filter((socket) =>
        socket.name.startsWith(`socket_${modelCode}`),
      )
    }

    return relevant
  }

  function findRelevantMeshes(modelCode, meshes) {
    if (!Array.isArray(meshes) || !modelCode) return []

    return meshes.filter((mesh) => mesh.name.startsWith(`${modelCode}_`))
  }

  // priming van de viewer sticky maken
  const [texturesBootstrapped, setTexturesBootstrapped] = useState(false)

  const controlsRef = useRef()
  const canvasRef = useRef()

  const modelsStructure = useMemo(() => {
    return models
      .map(
        ({ model }) =>
          model?.children?.map((mesh) => mesh.name).join(',') || '',
      )
      .join(',')
  }, [models])

  // wanneer de modellen structuur of het modellen aantal wijzigt, reset de mount tracker
  useEffect(() => {
    // console.log('sceneMounted resetten')
    mountedUUIDsRef.current.clear()
    setSceneMounted(false)
  }, [modelsStructure])

  // environment kan niet ingeladen worden
  const handleEnvError = useCallback(
    (error) => {
      // console.log('handleEnvError called with error:', error)
      if (!envError && onViewerError) {
        setEnvError(true)
        setError(error)
        onViewerError(`😣 Failed to load environment map: ${error}`)
      }
    },
    [envError, onViewerError],
  )

  // environment is klaar
  const handleEnvReady = useCallback(
    (ready) => {
      if (ready && !envError && !envReady) {
        console.log('✋ Environment ready.')
        setEnvReady(() => ready)
      }
    },
    [envError, envReady],
  )

  const materialTypes = useMemo(
    () => getMaterialTypes(filteredProductDataArray),
    [filteredProductDataArray],
  )

  // welke modellen hebben we nodig volgens de actieve productData
  useEffect(() => {
    const requiredCodes = new Set(
      filteredProductDataArray.flatMap((p) => [
        p.modelCode,
        ...(p.crossSellData || []).map((x) => x.modelCode),
      ]),
    )

    if (requiredCodes.size && modelsCount.current !== requiredCodes.size) {
      modelsCount.current = requiredCodes.size
    }
  }, [models, filteredProductDataArray])

  // material diffs
  function buildFullChangeSet(products) {
    const sel = buildSelectionMap(products)
    return {
      keys: new Set([...sel.keys()]),
      materials: new Set([...sel.values()].map((v) => v.material)),
    }
  }

  useEffect(() => {
    if (!filteredProductDataArray?.length) return

    const next = buildSelectionMap(filteredProductDataArray)

    if (!viewerReady) {
      setMaterialsMap(new Map())
      setTexturesLoaded(false)
      setTexturesBootstrapped(false)

      prevSelectionRef.current = new Map()

      const full = buildFullChangeSet(filteredProductDataArray)

      setChangeSet((prev) => ({
        keys: full.keys,
        materials: full.materials,
        version: prev.version + 1,
      }))

      prevSelectionRef.current = next
      return
    }

    const changedKeys = new Set()
    const changedMaterials = new Set()

    next.forEach((val, key) => {
      const prev = prevSelectionRef.current.get(key)
      if (!prev || prev.code !== val.code) {
        changedKeys.add(key)
        changedMaterials.add(val.material)
      }
    })

    if (changedKeys.size > 0) {
      setChangeSet((prev) => ({
        keys: changedKeys,
        materials: changedMaterials,
        version: prev.version + 1,
      }))
    }

    prevSelectionRef.current = next
  }, [filteredProductDataArray, viewerReady])

  const getGltfRoot = function (obj) {
    let o = obj
    while (o.parent && o.parent.type !== 'Scene') o = o.parent
    return o
  }

  const snapClipToTime = function (root, clip, t, trackFilter) {
    let tracks = clip.tracks

    if (typeof trackFilter === 'function') {
      tracks = clip.tracks.filter((tr) => trackFilter(tr))
    }

    const filteredClip = new AnimationClip(
      `${clip.name}__filtered`,
      clip.duration,
      tracks,
    )

    const mixer = new AnimationMixer(root)
    const action = mixer.clipAction(filteredClip)

    action.reset()
    action.setLoop(LoopOnce, 0)
    action.clampWhenFinished = true
    action.enabled = true
    action.play()

    mixer.setTime(t)
    mixer.update(0)

    action.paused = true
    root.updateMatrixWorld(true)
  }

  // bijhouden of de sockets al geset zijn voor de huidige materialen
  const socketsAttachKeyRef = useRef('')

  // socket attachment voor de xsells, alleen wanneer de structuur verandert, niet bij inladen materials
  useEffect(() => {
    // console.log(
    //   '- In socket application: ',
    //   envReady,
    //   modelsLoaded,
    //   sceneMounted,
    // )

    if (envError) return
    if (!envReady) return
    if (!modelsLoaded) return // modellen zijn geladen
    if (!sceneMounted) return // en modellen zijn gemount aan de scene, dus staan op z'n plaats, klaar om sockets te vullen
    if (filteredProductDataArray.length === 0) return

    // actieve xsells met een key bijhouden en daarop vergelijken
    const activeXsells = new Set(
      filteredProductDataArray.flatMap((p) =>
        (p.crossSellData || []).map((x) => x.modelCode),
      ),
    )
    const attachKey = modelsStructure + '|' + [...activeXsells].sort().join(',')

    // niks veranderd, dus bailen
    if (socketsAttachKeyRef.current === attachKey) return

    console.log('⚙️ Apply sockets (structure changed)')

    const sockets = models.flatMap(({ model }) =>
      (model?.children || []).filter(
        (child) => !child.isMesh && child.name.includes('socket_'),
      ),
    )

    const meshes = models.flatMap(({ model }) => {
      // console.log(model)
      return (model?.children || []).filter((child) => child.isMesh)
    })

    const animations = models.flatMap(
      (model) => model?.model?.userData?.animations || [],
      // model?.model?.userData?.animations
      //   ? {
      //       modelId: model?.product?.modelCode,
      //       animations: model.model.userData.animations,
      //     }
      //   : [],
    )

    sockets.sort((a, b) => {
      if (a.name < b.name) {
        return -1
      }
      if (a.name > b.name) {
        return 1
      }
      return 0
    })

    // meshes.sort((a, b) => {
    //   if (a.name < b.name) {
    //     return -1
    //   }
    //   if (a.name > b.name) {
    //     return 1
    //   }
    //   return 0
    // })

    console.log('👀 all sockets: ', [...sockets])
    console.log('👀 all meshes: ', [...meshes])
    console.log('👀 all animations: ', [...animations])

    // sockets setten
    filteredProductDataArray.forEach((product) => {
      if (!product.crossSellData?.length) return

      product.crossSellData.forEach((crossSellItem) => {
        let relevantSockets = findRelevantSockets(
          crossSellItem.modelCode,
          sockets,
        )

        if (!relevantSockets.length) return

        relevantSockets.sort((a, b) => a.name.length - b.name.length)

        const xsell = models.find(
          (m) => m.product.modelCode === crossSellItem.modelCode,
        )
        if (!xsell) return

        // we hebben het model, en gevonden sockets
        for (let i = 0; i < relevantSockets.length; i++) {
          const socket = relevantSockets[i]

          // is er al 1, loop dan door
          const exists = socket.children.find((child) =>
            child.name?.startsWith(xsell.product.modelCode),
          )
          if (exists) continue

          if (i === 0) {
            // console.log(
            //   `filling socket: ${socket.name} with ${xsell.product.modelCode}`,
            // )
            // originele 1x
            socket.add(xsell.model)
            xsell.model.matrix.identity()
            xsell.model.updateMatrixWorld(true)
          } else {
            // klonen voor bijkomende sockets
            // console.log(
            //   `filling cloned socket: ${socket.name} with ${xsell.product.modelCode}`,
            // )
            const clone = xsell.model.clone(true) // deep clone
            makeMaterialsUnique(clone)
            clone.name = xsell.model.name // naam bijhouden zodat existingChild werkt
            socket.add(clone)
            clone.matrix.identity()
            clone.updateMatrixWorld(true)
          }

          if (animations.length && isSet) {
            const animation = animations.find((c) => {
              let animation_name = c.name.replace('Action', '')
              animation_name = animation_name.replace(/\.\d{3}/, '')
              return socket.name.startsWith(animation_name)
            })
            if (animation) {
              snapClipToTime(getGltfRoot(socket), animation, 0.5) // frame 15 in een 60 frame animation (30fps)
            }
          }
        }
      })
    })

    refreshMeshes()

    // structuur is toegepast, referentie setten
    socketsAttachKeyRef.current = attachKey
  }, [
    envReady,
    envError,
    modelsLoaded,
    modelsStructure,
    filteredProductDataArray,
    sceneMounted,
  ])

  // een soort ready functie, maar zonder camera update, daarop wordt nog eens gewacht
  const isPrimed = useMemo(() => {
    return (
      envReady &&
      !envError &&
      texturesBootstrapped &&
      modelsLoaded &&
      filteredProductDataArray.length > 0
    )
  }, [
    envReady,
    envError,
    texturesBootstrapped,
    modelsLoaded,
    filteredProductDataArray.length,
  ])

  // textures laadfunctie
  useEffect(() => {
    // console.log(
    //   '- In textures load functie: ',
    //   !envReady,
    //   envError,
    //   modelsLoaded,
    //   changeSet.version,
    // )
    if (!envReady || envError) return
    if (!modelsLoaded) return
    if (changeSet.version === 0) return

    let cancelled = false

    let published = false

    const controller = requestScope.createController()

    const loadedTextures = new Set()

    console.log(`changeSet materials: `, changeSet.materials)

    const toLoad = [...changeSet.materials].filter(
      (mat) => !materialsMap.has(mat),
    )

    // houd een versie van de textures bij per changeSet.version
    if (toLoad.length === 0) {
      if (lastBumpedVersionRef.current !== changeSet.version) {
        bumpTexturesVersion((v) => v + 1)
        lastBumpedVersionRef.current = changeSet.version
        // eerste keer, dus bootstrappen
        if (!texturesBootstrapped) {
          setTexturesLoaded(true)
          setTexturesBootstrapped(true)
        }
      }
      requestScope.release(controller)

      return
    }

    // versie onthouden
    inFlightVersionRef.current = changeSet.version

    if (!texturesBootstrapped) setTexturesLoaded(false) // enkel voor eerste bootstrap

    const newMap = new Map(materialsMap)

    Promise.all(
      toLoad.map((item) => {
        const matObjOrId = item.material ?? item
        const label =
          item.code ??
          item?.material?.code ??
          matObjOrId?.code ??
          String(matObjOrId)

        console.log(`🟡 Requesting textures for: ${label}`)

        return loadTexturesFromJSON(
          matObjOrId,
          resolution,
          (err) => {
            if (!controller.signal.aborted && err) {

              console.error(`🔴 Error while requesting ${label}:`, err)

            }
          },
          controller.signal,
        )
          .then((textures) => {
            if (cancelled || controller.signal.aborted) {

              disposeTextures(textures)

              return

            }

            loadedTextures.add(textures)

            if (item.material) {
              newMap.set(item.material, { ...item, textures, ready: true })
            } else {
              newMap.set(matObjOrId, {
                material: matObjOrId,
                textures,
                ready: true,
              })
            }
            console.log(`🟢 Loaded textures for: ${label}`)
          })
          .catch((err) => {
            if (!cancelled && !isAbortError(err)) {

              console.error(`🔴 Failed to load textures for: ${label}`, err)

            }
          })
      }),
    )
      .then(() => {
        if (cancelled || controller.signal.aborted) return
        // als er een nieuwe texture selectie is nu deze kickstart, dit negeren
        if (inFlightVersionRef.current !== changeSet.version) return

        materialsMapRef.current = newMap
        published = true

        setMaterialsMap(newMap)
        setTexturesLoaded(true)
        setTexturesBootstrapped(true)

        // 1 keer bumpen voor deze versie
        if (lastBumpedVersionRef.current !== changeSet.version) {
          bumpTexturesVersion((v) => v + 1)
          lastBumpedVersionRef.current = changeSet.version
        }
      })
      .catch((error) => {
        if (!cancelled && !controller.signal.aborted && !isAbortError(error)) {

          onViewerErrorRef.current?.(error)

        }
      })
      .finally(() => requestScope.release(controller))

    return () => {
      cancelled = true
      controller.abort()
      requestScope.release(controller)

      if (!published) {

        loadedTextures.forEach(disposeTextures)

      }
    }
  }, [
    changeSet.version,
    changeSet.materials,
    envReady,
    envError,
    modelsLoaded,
    materialsMap,
    resolution,
    texturesBootstrapped,
    requestScope,
  ])

  const makeMaterialsUnique = function (root) {
    root.traverse((o) => {
      if (!o.isMesh) return

      if (Array.isArray(o.material)) {
        o.material = o.material.map((m) => m?.clone?.() ?? m)
      } else if (o.material?.clone) {
        o.material = o.material.clone()
      }
    })
  }

  // materials toepassen als texturen/modellen veranderen
  useEffect(() => {
    // console.log(
    //   '- In materials apply functie: ',
    //   modelsLoaded,
    //   texturesLoaded,
    //   changeSet.version,
    // )
    if (!modelsLoaded) return
    if (!texturesLoaded) return
    if (changeSet.version === 0) return

    // kijken of een aangepast materiaal al geladen is of niet, indien niet, wachten op materialsMap update
    const neededMats = [...changeSet.materials]
    const allReady = neededMats.every((mat) => materialsMap.has(mat))
    if (!allReady) return

    // vind changed pairs
    const changedKeySet = changeSet.keys

    // console.log(`changeSet keys: `, changedKeySet)
    // console.log(meshes)

    if (filteredProductDataArray && filteredProductDataArray.length > 0) {
      const products = filteredProductDataArray.flatMap((product) => [
        product,
        ...(product.crossSellData || []),
      ])
      products.forEach((product) => {
        meshes.forEach((mesh) => {
          const type = mesh.name.replace(`${product.modelCode}_`, '')
          const key = `${product.modelCode}:${type}`

          // const parent_name =
          //   mesh?.parent?.parent?.parent !== null
          //     ? mesh?.parent?.parent?.parent?.name
          //     : 'root'
          // const parent_key = `${parent_name}:${type}`

          // console.log(`👀 ${key}`)
          // console.log(`👀 ${parent_key}`)

          if (
            !changedKeySet.has(key) // enkel vergelijken op level 1
            // !changedKeySet.has(key) && // bij cushion sets worden level
            // !changedKeySet.has(parent_key)
          ) {
            // console.log(`❌ ${key} zit al in de changeset, dus skippen`)
            return // niet om aan te passen, niet gevonden, dus skippen
          }

          // vind het nieuwe geselecteerde materiaal voor dit paar
          const correspondingProductData = findProductData(
            // !changedKeySet.has(parent_key)
            //   ? product.modelCode
            //   : mesh?.parent?.parent?.parent?.name,
            product.modelCode,
            filteredProductDataArray,
          )

          // console.log(correspondingProductData)

          const newMaterialData = correspondingProductData?.materialData?.find(
            ({ type: t }) => t === type,
          )

          if (!newMaterialData) return

          // indien al toegepast, skippen
          if (mesh.material?.name === newMaterialData.code) return

          const entry = materialsMap.get(newMaterialData.material)
          if (!entry?.textures) return // sowieso ready door allReady

          if (entry?.material?.code && mesh?.name) {
            console.log(
              `🖼️ Applying material ${entry.material.code} on model ${mesh.name} (${key})`,
            )
          }

          applyMaterial(
            mesh,
            meshes,
            mesh.material,
            entry.material,
            entry.textures,
            (error) => {
              setError(error)
              onViewerError && onViewerError(error)
            },
            () => {
              const oldMaterialKey = mesh.material.name
              unloadTextures(oldMaterialKey, materialsMap) // oude eruit
              mesh.material.name = entry.material.code
            },
          )
        })
      })
    }

    // models.forEach(({ model, product }) => {
    //   model?.traverse((child) => {
    //     if (!child.isMesh) return
    //
    //     // wat is deze mesh's (modelCode:type) key
    //     const type = child.name.replace(`${product.modelCode}_`, '')
    //     const key = `${product.modelCode}:${type}`
    //
    //     const parent_key = `${child?.parent?.parent?.parent?.name}:${type}` // voor als je de parent moet kunnen aanpassen, zodat het naar 3 levels lager aangepast wordt (level 3 situaties bij cushion sets): model > socket > model > mesh
    //
    //     // console.log(`key vs. parent`, key, parent_key)
    //
    //     if (
    //       //!changedKeySet.has(key) // enkel vergelijken op level 1
    //       !changedKeySet.has(key) && // bij cushion sets worden level
    //       !changedKeySet.has(parent_key)
    //     ) {
    //       // console.log(`❌ ${key} zit al in de changeset, dus skippen`)
    //       return // niet om aan te passen, niet gevonden, dus skippen
    //     }
    //
    //     // vind het nieuwe geselecteerde materiaal voor dit paar
    //     const correspondingProductData = findProductData(
    //       !changedKeySet.has(parent_key)
    //         ? product.modelCode
    //         : child?.parent?.parent?.parent?.name,
    //       //product.modelCode,
    //       filteredProductDataArray,
    //     )
    //
    //     // console.log(correspondingProductData)
    //
    //     const newMaterialData = correspondingProductData?.materialData?.find(
    //       ({ type: t }) => t === type,
    //     )
    //
    //     if (!newMaterialData) return
    //
    //     // indien al toegepast, skippen
    //     if (child.material?.name === newMaterialData.code) return
    //
    //     const entry = materialsMap.get(newMaterialData.material)
    //     if (!entry?.textures) return // sowieso ready door allReady
    //
    //     if (entry?.material?.code && model?.name && child?.name) {
    //       console.log(
    //         `🖼️ Applying material ${entry.material.code} on model ${model.name} - ${child.name}`,
    //       )
    //     }
    //
    //     applyMaterial(
    //       model,
    //       child.material,
    //       entry.material,
    //       entry.textures,
    //       (error) => {
    //         setError(error)
    //         onViewerError && onViewerError(error)
    //       },
    //       () => {
    //         const oldMaterialKey = child.material.name
    //         unloadTextures(oldMaterialKey, materialsMap) // oude eruit
    //         child.material.name = entry.material.code
    //       },
    //     )
    //   })
    // })
  }, [
    models,
    filteredProductDataArray,
    resolution,
    texturesVersion,
    modelsLoaded,
    texturesLoaded,
    onViewerError,
    envReady,
    materialTypes,
    materialsMap,
  ])

  // xsell visibiliteit, runt als productData wordt aangepast of modellen veranderen
  useEffect(() => {
    if (!models.length) return
    if (!modelsLoaded) return
    if (!texturesLoaded) return
    if (!filteredProductDataArray.length) return

    const mainCode = filteredProductDataArray[0]?.modelCode
    if (!mainCode) return

    // actieve xsell codes voor de huidige selectie
    const activeXsells = new Set(
      (filteredProductDataArray[0]?.crossSellData || []).map(
        (x) => x.modelCode,
      ),
    )

    console.log(`👀 in visibility check`)

    // console.log(
    //   '- hidden xsells productdata: ',
    //   filteredProductDataArray[0],
    //   activeXsells,
    // )

    let shapekey_morphs = []

    // visibiliteit checken van xsells
    models.forEach(({ model }) => {
      model?.traverse((child) => {
        /*if (child.isMesh) {
          allMeshes.push(child)
        } else  if (!child.isMesh && child.name.includes('socket_')) {
          allSockets.push(child)
        }*/

        if (!child.isMesh) return

        // alle meshes altijd standaard zichtbaar maken
        child.visible = true

        // ^[A-Z0-9]+[_a-z]+ matched met "PILBRH6040P_colour_cushion", dan en enkel dan een check doen
        const xsellCodeRegEx = /^[A-Z0-9]+_[a-z]/

        // console.log('model: ' + model.name + ', mesh: ' + child.name)

        // vind je een mesh waarbij de naam een configureerbare notatie heeft, check dan of die begint met de mainCode of niet in de xsells zit
        if (
          !child.name.startsWith(mainCode) &&
          xsellCodeRegEx.test(child.name)
        ) {
          // console.log('mesh in check: ' + child.name)

          child.visible = [...activeXsells].some((code) =>
            child.name.split('_').includes(code),
          )

          xsellCodeRegEx.lastIndex = 0
        }

        // check of een mesh een morph heeft en als dat zo is, doe het nodige op basis van de morph layer name "[modelsku]_[value_if_modelskupresentandvisible]"
        if (child?.morphTargetDictionary && child?.morphTargetInfluences) {
          console.log(
            `👀 morphs found`,
            child.morphTargetDictionary,
            child.morphTargetInfluences,
          )
          shapekey_morphs.push(child)
        }
      })
    })

    if (shapekey_morphs.length) {
      shapekey_morphs.forEach((child_with_shapekey, index) => {
        Object.keys(child_with_shapekey.morphTargetDictionary).forEach(
          (value, index) => {
            let modelsku_and_value = value.split('_')
            if (modelsku_and_value.length === 2) {
              // zoek naar het model met deze sku
              // bijvoorbeeld, als de shapekey "SKU_0" noemt, zetten we de shapekey op 0 als SKU aanwezig is in de scene, of op 1 indien niet aanwezig
              const meshes_found = findRelevantMeshes(
                modelsku_and_value[0],
                meshes,
              )

              const value_present = parseInt(modelsku_and_value[1])
              const value_not_present = value_present ^ 1
              const mesh_visible =
                meshes_found.length && meshes_found[0].visible

              console.log(
                `👀 meshes found?`,
                meshes_found,
                mesh_visible,
                mesh_visible ? value_present : value_not_present,
              )
              child_with_shapekey.morphTargetInfluences[index] = mesh_visible
                ? value_present
                : value_not_present
            }
          },
        )
      })
    }

    // if (animation_child && animation_mesh) {
    //   console.log(animation_child.visible)
    //
    //   const strap_mesh = animation_mesh.children[0]
    //
    //   const idx = strap_mesh.morphTargetDictionary['TightStrap']
    //   if (idx !== undefined) {
    //     strap_mesh.morphTargetInfluences[idx] = animation_child.visible ? 0 : 1
    //   }
    //
    // }

    // enkel voor sets, loop de crossselldata en check of er voor elke xsell een socket is én mesh, indien geen van beide = hidden
    // if (isSet) {
    //   activeXsells.forEach((xsell) => {
    //     let relevantSockets = findRelevantSockets(xsell, allSockets)
    //     // let relevantMeshes = findRelevantMeshes(xsell, allMeshes)
    //     let xsell_visible = true
    //
    //     if (!relevantSockets.length /*&& !relevantMeshes.length*/) {
    //       const xsell_model = models.find((m) => m.product.modelCode === xsell)
    //       if (xsell_model) {
    //         xsell_model.model.visible = false
    //         xsell_visible = false
    //       }
    //     }
    //     console.log(`👀 xsell ${xsell} visible:`, xsell_visible)
    //   })
    // }
  }, [models, modelsLoaded, texturesLoaded, filteredProductDataArray])

  // houd modelrequests bij per code, zodat een gereed deelmodel andere downloads niet herstart.
  useEffect(() => {
    const requests = modelRequestsRef.current

    const requiredCodes = new Set(JSON.parse(requiredModelCodes))

    requests.forEach((request, code) => {
      if (!requiredCodes.has(code) || !envReady || envError) {

        request.controller.abort()
        requestScope.release(request.controller)
        requests.delete(code)

      }
    })

    if (!envReady || envError) {

      return

    }

    const products = productDataRef.current.flatMap((product) => [
      product,
      ...(product.crossSellData || []),
    ])

    products.forEach((product) => {
      if (
        requests.has(product.modelCode) ||
        modelsRef.current.some((item) => item.product.modelCode === product.modelCode)
      ) {

        return

      }

      const controller = requestScope.createController()

      const request = { controller }

      requests.set(product.modelCode, request)

      loadModel(product.modelCode, undefined, undefined, undefined, controller.signal)
        .then((model) => {
          if (controller.signal.aborted) {

            disposeModel(model)

            return

          }

          model.name = product.modelCode

          ownedModelsRef.current.add(model)

          setModels((previous) => [
            ...previous,
            { model, product, ready: true },
          ])
        })
        .catch((error) => {
          if (!controller.signal.aborted && !isAbortError(error)) {

            console.error(`😣 Error loading model ${product.modelCode}:`, error)
            onViewerErrorRef.current?.(error)

          }
        })
        .finally(() => {
          requestScope.release(controller)

          // een oude request mag een nieuwe request met dezelfde code niet verwijderen.
          if (requests.get(product.modelCode) === request) {

            requests.delete(product.modelCode)

          }
        })
    })
  }, [requiredModelCodes, envReady, envError, requestScope])

  const getExcludeList = useCallback(() => {
    const sceneObjects = []

    // neem ook hulpmiddelen buiten de productmodellen mee, zoals het schaduwvlak.
    sceneRef.current?.traverse((obj) => sceneObjects.push(obj))

    return sceneObjects.filter((obj) => obj.userData?.noExport)
  }, [])

  const buildViewerDataSnapshot = useCallback(() => {
    const controls = controlsRef.current
    const cam = controls?.object
    const target = controls?.target

    // orbit / camera
    const orbit = controls
      ? {
          target: target ? [target.x, target.y, target.z] : [0, 0, 0],
          theta: controls.getAzimuthalAngle?.() ?? null,
          phi: controls.getPolarAngle?.() ?? null,
          radius:
            cam?.position?.distanceTo && target
              ? cam.position.distanceTo(target)
              : null,
        }
      : null

    const camera = cam
      ? {
          fov: cam.fov,
          aspect: cam.aspect,
          up: cam.up?.toArray?.(),
          position: cam.position?.toArray?.(),
        }
      : null

    // bounds
    const box = new Box3()
    let hasBounds = false

    models.forEach(({ model }) => {
      if (!model) return

      model.updateMatrixWorld(true)

      const modelBox = new Box3().setFromObject(model, true)
      if (modelBox.isEmpty()) return

      if (!hasBounds) {
        box.copy(modelBox)
        hasBounds = true
      } else {
        box.union(modelBox)
      }
    })

    const size = new Vector3()
    const center = new Vector3()

    if (hasBounds) {
      box.getSize(size)
      box.getCenter(center)
    }

    const bounds = hasBounds
      ? {
          min: box.min.toArray(),
          max: box.max.toArray(),
          size: size.toArray(),
          center: center.toArray(),
        }
      : null

    const productData = filteredProductDataArray

    const buildData = { productData, orbit, camera, bounds }

    console.log(`👀 data is requested:`, buildData)

    return buildData
  }, [models, filteredProductDataArray])

  // API
  useEffect(() => {
    if (!onViewerAPI) return
    // if (viewerReady) console.log(`👀 API is ready`)
    const api = {
      isReady: viewerReady,
      getDataSnapshot: () => structuredClone(buildViewerDataSnapshot()),
    }
    onViewerAPI(api)
  }, [onViewerAPI, viewerReady, buildViewerDataSnapshot])

  return (
    <div className={containerClassName}>
      <Canvas
        shadows
        gl={{ alpha: true }}
        ref={canvasRef}
        className={className}
        camera={{ fov: 30 }}
        style={{
          opacity: viewerReady ? 1 : 0,
        }}
        onCreated={(state) => {
          sceneRef.current = state.scene
        }}
      >
        <directionalLight
          castShadow
          position={[5, 10, 5]}
          shadow-mapSize={[1024, 1024]}
          intensity={0.5}
        >
          <orthographicCamera
            attach="shadow-camera"
            args={[-6, 6, 6, -6, 1, 20]}
          />
        </directionalLight>

        <>
          {models.map(({ model }, index) =>
            model ? (
              // deze models map runt telkens de models var is aangepast, dus telkens 1 model ingeladen wordt, loopt deze map opnieuw, niet ideaal maar het zij zo.
              <primitive
                key={index}
                object={model}
                // R3F attached het object in de scene
                ref={(obj) => {
                  if (!obj) return // wordt ook gecalled voor unmounts, dat negeren
                  const set = mountedUUIDsRef.current
                  if (!set.has(obj.uuid)) {
                    set.add(obj.uuid)
                    if (set.size === models.length) {
                      setSceneMounted(true) // alle <primitive>s zitten in de scene en zijn geattacheerd, sockets kunnen geset worden
                      if (modelsCount.current === set.size) {
                        setModelsLoaded(true)
                        console.log(
                          '🥳 All models are mounted, ready for socket attachment',
                        )
                      }
                    }
                  }
                }}
              />
            ) : null,
          )}
        </>

        <Env
          background={false}
          map={environment}
          rotation={-45}
          onDone={handleEnvReady}
          onError={handleEnvError}
          requestScope={requestScope}
        />

        <OrbitControls
          ref={controlsRef}
          enabled={viewerReady}
          minPolarAngle={-Math.PI / 0.5}
          maxPolarAngle={Math.PI / 2}
          enablePan={false}
          maxDistance={10}
          minDistance={1}
          autoRotate={false}
        />

        <CameraUpdater
          models={models}
          modelsStructure={modelsStructure}
          controlsRef={controlsRef}
          viewerPrimed={isPrimed}
          onSettled={() => {
            if (unmountedRef.current) {

              return

            }

            if (!viewerReady) {
              setViewerReady(true)
              if (!needDataOnViewerReady) {
                onViewerReady?.(true) // belangrijk in zowel front als Puppeteer dat viewer hierna pas zichtbaar is
              } else {
                readyFrameRef.current = requestAnimationFrame(() => {
                  readyFrameRef.current = null

                  if (unmountedRef.current) {

                    return

                  }

                  const snapshot = buildViewerDataSnapshot() // nodig voor blender, we sturen zowel de productData als de camera positie terug
                  onViewerReady?.(snapshot)
                })
              }
            }
          }}
        />
        <CaptureComponent
          canvas={canvasRef}
          onCapture={onCapture}
          viewerReady={viewerReady}
        />

        <RendererSettings toneMappingExposure={2} shadowMap />

        {modelsLoaded && castShadow && (
          <mesh
            receiveShadow
            rotation-x={-Math.PI / 2}
            userData={{ noExport: true }}
          >
            <planeGeometry args={[7, 7]}></planeGeometry>
            <shadowMaterial opacity={0.075} />
          </mesh>
        )}

        <ExportBridge
          ready={viewerReady}
          onARExport={onARExport}
          getExcludeList={getExcludeList}
        />

        {isDevelopment && <RendererInfo materialsMap={materialsMap} />}
      </Canvas>
    </div>
  )
}
